import { useEffect, useState } from "react";
import { HeartPulse, Plus, Trash2, Pencil, Lock, AlarmClock, UserRound, Users, Baby, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import PhotoAttach from "../PhotoAttach";
import { useTravelers } from "./useTravelers";

/* Travel / Medical Insurance vault — multiple policies, private to the user. */
const inp = "border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary bg-white";
const empty = { company: "", policy_number: "", valid_from: "", valid_to: "", covered_self: true, covered_spouse: false, children: [], traveler_names: [], notes: "" };

const ValidityBadge = ({ it }) => {
  const d = it.days_to_expiry;
  if (d == null) return null;
  const cls = d < 0 ? "bg-rose-100 text-rose-700" : d <= 30 ? "bg-amber-100 text-amber-800" : "bg-emerald-50 text-emerald-700";
  const label = d < 0 ? `Expired ${it.valid_to}` : d === 0 ? "Expires today" : d <= 30 ? `Expires in ${d} day${d === 1 ? "" : "s"}` : `Valid until ${it.valid_to}`;
  return <span className={`inline-flex items-center gap-1 text-[11px] font-700 px-1.5 py-0.5 rounded ${cls}`} data-testid={`insurance-validity-${it.id}`}><AlarmClock className="w-3 h-3" /> {label}</span>;
};

export default function InsuranceManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const travelers = useTravelers(open);

  const load = () => api.get("/insurance").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.company.trim()) { toast.error("Insurance company name is required"); return; }
    try {
      await api.post("/insurance", form);
      toast.success(form.id ? "Insurance policy updated" : "Insurance policy added");
      setForm(empty); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save policy"); }
  };

  const edit = (it) => { setForm({ ...empty, ...it }); setOpen(true); };
  const remove = async (id) => { await api.delete(`/insurance/${id}`); toast.success("Policy removed"); load(); };

  const setChild = (i, patch) => setForm((f) => ({ ...f, children: f.children.map((c, j) => (j === i ? { ...c, ...patch } : c)) }));

  return (
    <div data-testid="insurance-manager" className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-900 text-[#4285F4] text-2xl flex items-center gap-2"><HeartPulse className="w-6 h-6" /> Travel &amp; Medical Insurance</h2>
        <button onClick={() => { setForm(empty); setOpen((o) => !o); }} data-testid="insurance-new"
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary text-white text-sm font-600 hover:bg-blue-700"><Plus className="w-4 h-4" /> Add policy</button>
      </div>
      <p className="text-sm text-muted-foreground mb-4 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Private to you. Keep every travel or medical insurance policy and who it covers in one place.</p>

      {open && (
        <div className="border border-teal-300 bg-white p-4 mb-4" data-testid="insurance-form">
          <div className="grid sm:grid-cols-2 gap-2">
            <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Insurance company name *" data-testid="insurance-company" className={inp} />
            <input value={form.policy_number} onChange={(e) => setForm({ ...form, policy_number: e.target.value })} placeholder="Policy number" data-testid="insurance-policy-number" className={inp} />
            <label className="text-xs text-muted-foreground">Valid from
              <input type="date" value={form.valid_from || ""} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} data-testid="insurance-valid-from" className={`${inp} w-full mt-1`} />
            </label>
            <label className="text-xs text-muted-foreground">Valid to
              <input type="date" value={form.valid_to || ""} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} data-testid="insurance-valid-to" className={`${inp} w-full mt-1`} />
            </label>
          </div>

          <div className="mt-3 border-t border-teal-200 pt-3">
            <div className="text-xs font-700 text-teal-800 flex items-center gap-1.5 mb-2"><Users className="w-3.5 h-3.5" /> Covered passengers</div>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="inline-flex items-center gap-1.5 cursor-pointer" data-testid="insurance-covered-self">
                <input type="checkbox" checked={!!form.covered_self} onChange={(e) => setForm({ ...form, covered_self: e.target.checked })} /> Self
              </label>
              <label className="inline-flex items-center gap-1.5 cursor-pointer" data-testid="insurance-covered-spouse">
                <input type="checkbox" checked={!!form.covered_spouse} onChange={(e) => setForm({ ...form, covered_spouse: e.target.checked })} /> Spouse
              </label>
            </div>
            {travelers.length > 0 && (
              <div className="mt-2" data-testid="insurance-travelers-pick">
                <div className="text-[11px] text-muted-foreground mb-1">Saved travellers covered by this policy:</div>
                <div className="flex flex-wrap gap-1.5">
                  {travelers.map((t) => {
                    const on = (form.traveler_names || []).includes(t.name);
                    return (
                      <button key={t.id} type="button" data-testid={`insurance-traveler-${t.id}`}
                        onClick={() => setForm((f) => ({ ...f, traveler_names: on ? f.traveler_names.filter((n) => n !== t.name) : [...(f.traveler_names || []), t.name] }))}
                        className={`text-xs px-2 py-1 border transition-colors ${on ? "bg-teal-600 text-white border-teal-600" : "border-zinc-300 bg-white hover:border-teal-400"}`}>
                        {t.name}{t.relation ? ` · ${t.relation}` : ""}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="mt-2 space-y-1.5">
              {form.children.map((c, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`insurance-child-${i}`}>
                  <Baby className="w-3.5 h-3.5 text-teal-600 shrink-0" />
                  <input value={c.name} onChange={(e) => setChild(i, { name: e.target.value })} placeholder="Child name" data-testid={`insurance-child-name-${i}`} className={`${inp} flex-1 min-w-0`} />
                  <input value={c.age || ""} onChange={(e) => setChild(i, { age: e.target.value })} placeholder="Age" data-testid={`insurance-child-age-${i}`} className={`${inp} w-16`} />
                  <label className="inline-flex items-center gap-1 text-xs cursor-pointer shrink-0">
                    <input type="checkbox" checked={c.covered !== false} onChange={(e) => setChild(i, { covered: e.target.checked })} data-testid={`insurance-child-covered-${i}`} /> Covered
                  </label>
                  <button onClick={() => setForm((f) => ({ ...f, children: f.children.filter((_, j) => j !== i) }))} className="text-muted-foreground hover:text-destructive"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <button onClick={() => setForm((f) => ({ ...f, children: [...f.children, { name: "", age: "", covered: true }] }))} data-testid="insurance-add-child"
                className="inline-flex items-center gap-1 text-xs text-teal-700 border border-teal-300 px-2 py-1 hover:bg-teal-50"><Plus className="w-3 h-3" /> Add child</button>
            </div>
          </div>

          <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (coverage details, emergency phone, exclusions…)" data-testid="insurance-notes" rows={2}
            className={`${inp} w-full mt-3 resize-y`} />

          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-zinc-300 text-sm">Cancel</button>
            <button onClick={save} data-testid="insurance-save" className="px-4 py-2 bg-primary text-white text-sm font-600 hover:bg-blue-700">{form.id ? "Update" : "Save"} policy</button>
          </div>
        </div>
      )}

      {items.length === 0 && !open ? (
        <p className="text-sm text-muted-foreground">No insurance policies yet. Add your travel or medical insurance so the details are always at hand when you need them.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map((it) => (
            <div key={it.id} className="border border-zinc-300 bg-white p-4" data-testid={`insurance-card-${it.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-900 text-lg truncate">{it.company}</div>
                  {it.policy_number && <div className="text-xs font-mono text-muted-foreground">Policy {it.policy_number}</div>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => edit(it)} data-testid={`insurance-edit-${it.id}`} className="text-muted-foreground hover:text-primary p-1"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(it.id)} data-testid={`insurance-remove-${it.id}`} className="text-muted-foreground hover:text-destructive p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="mt-2 space-y-1.5 text-sm">
                <ValidityBadge it={it} />
                {(it.valid_from || it.valid_to) && (
                  <div><span className="text-muted-foreground">Validity: </span><span className="font-600">{it.valid_from || "—"} → {it.valid_to || "—"}</span></div>
                )}
                <div className="flex flex-wrap gap-1.5" data-testid={`insurance-covered-${it.id}`}>
                  {it.covered_self && <span className="inline-flex items-center gap-1 text-[11px] font-600 bg-teal-50 text-teal-700 px-1.5 py-0.5 rounded"><UserRound className="w-3 h-3" /> Self</span>}
                  {it.covered_spouse && <span className="inline-flex items-center gap-1 text-[11px] font-600 bg-teal-50 text-teal-700 px-1.5 py-0.5 rounded"><Users className="w-3 h-3" /> Spouse</span>}
                  {(it.children || []).map((c, i) => (
                    <span key={i} className={`inline-flex items-center gap-1 text-[11px] font-600 px-1.5 py-0.5 rounded ${c.covered !== false ? "bg-teal-50 text-teal-700" : "bg-zinc-100 text-zinc-500 line-through"}`}>
                      <Baby className="w-3 h-3" /> {c.name}{c.age ? ` (${c.age})` : ""}
                    </span>
                  ))}
                  {(it.traveler_names || []).map((n, i) => (
                    <span key={`t-${i}`} className="inline-flex items-center gap-1 text-[11px] font-600 bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded"><UserRound className="w-3 h-3" /> {n}</span>
                  ))}
                  {!it.covered_self && !it.covered_spouse && (it.children || []).length === 0 && (it.traveler_names || []).length === 0 && <span className="text-xs text-muted-foreground">No passengers marked as covered</span>}
                </div>
                {it.notes && <div className="text-muted-foreground text-xs whitespace-pre-line">{it.notes}</div>}
                <PhotoAttach items={it.photos || []} testPrefix={`insurance-photo-${it.id}`} label="Add a screenshot / image"
                  onUpload={async (file) => { const fd = new FormData(); fd.append("file", file); await api.post(`/insurance/${it.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 }); load(); }}
                  onDelete={async (pid) => { await api.delete(`/insurance/${it.id}/photos/${pid}`); load(); }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
